/**
 * PromotionShareCard — multi-platform share-image generator for martial-arts promotions.
 *
 * Renders a pure-SVG promotion card (no external deps; brand-perfect; lossless),
 * converts it to a PNG blob via canvas drawImage, and offers the user every
 * sharing surface in one modal:
 *   - Native Web Share (covers iOS/Android share sheet → IG, FB, X, Threads,
 *     Messenger, iMessage, AirDrop, Discord — all from one button)
 *   - Download PNG (desktop fallback, also handles platforms the share sheet misses)
 *   - Copy to clipboard (image blob — paste straight into anywhere)
 *
 * Two aspect ratios are baked in:
 *   - 1080×1080 (Instagram Feed, Facebook, X/Twitter, LinkedIn)
 *   - 1080×1920 (Instagram Story, TikTok, Snapchat Story)
 *
 * Anti-shame tone — no "crushed", no "smashed". The copy is "Tempered to {Belt}"
 * because martial-arts promotions are about earning the temper, not winning over
 * a leaderboard.
 *
 * This component is shape-portable: future surfaces (PR cards, milestone cards,
 * adaptive-mode "regained a movement" cards) can reuse the same SVG pipeline by
 * swapping the `renderCardSVG()` body. The share/download/copy plumbing stays.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Share2, Download, Copy, Check, X, Image as ImageIcon, Smartphone, Megaphone } from 'lucide-react';
import QRCode from 'qrcode';
import { taglineFor } from '../utils/taglines';
import { useProfile } from '../context/ProfileContext';

const isDarkBeltColor = (hex) => {
  const c = (hex || '').toLowerCase();
  return c === '#0a0a0a' || c === '#78350f' || c === '#1e40af' || c === '#0c4a6e' ||
         c === '#6b21a8' || c === '#7c2d12' || c === '#581c87' || c === '#991b1b' ||
         c === '#2563eb' || c === '#0369a1';
};

const formatTenure = (fromISO, toDate) => {
  if (!fromISO) return null;
  const from = new Date(fromISO);
  if (Number.isNaN(from.getTime())) return null;
  const to = toDate || new Date();
  const ms = to.getTime() - from.getTime();
  if (ms < 0) return null;
  const days = Math.floor(ms / 86400000);
  if (days < 31) return days === 0 ? 'today' : `${days} days`;
  const months = Math.floor(days / 30.437);
  if (months < 12) return `${months} months`;
  const years = Math.floor(months / 12);
  const remMo = months % 12;
  return remMo === 0 ? `${years} years` : `${years}y ${remMo}mo`;
};

const formatLongDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

// Escape XML special chars so user-supplied strings can't break the SVG
const xml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');

/**
 * Build the SVG card markup as a string. ratio = 'square' (1080×1080)
 * or 'story' (1080×1920). Returns SVG XML ready for blob conversion.
 *
 * `mode` = 'promotion' (logged event with date/school/etc) or 'current' (sharing
 * current rank state without a logged promotion — headline shifts accordingly).
 * `premium` = true removes the bottom "enferno.app" URL + tagline. Wordmark stays
 * small at the bottom as a brand integrity floor — full white-label tier would
 * be a separate upgrade.
 */
const renderCardSVG = ({ ratio, art, rank, promotion, tenureFromPrev, athleteName, mode = 'promotion', premium = false, tagline, qrDataUrl, headline }) => {
  const W = 1080;
  const H = ratio === 'story' ? 1920 : 1080;
  const beltColor = rank?.color || '#27272a';
  const accent = art?.accent || '#ef4444';
  const darkBelt = isDarkBeltColor(beltColor);
  const tapeColor = darkBelt ? '#f5f5f4' : '#0a0a0a';
  const stripeCount = promotion.stripes || 0;
  const maxStripes = art?.hasStripes ? art.maxStripes : 0;

  // Belt strip — center-mounted, wide and tall enough to feel like fabric
  const beltY = ratio === 'story' ? 880 : 480;
  const beltH = 110;
  const beltW = 880;
  const beltX = (W - beltW) / 2;
  // Tape band sits at the right end of the belt. Sized to fit the vertical
  // stripe pips with sensible padding (the black stripe on a real gi belt is
  // ~3-4 inches; ours is proportional to the pip count).
  const tapeW = maxStripes > 0 ? Math.min(beltW * 0.40, maxStripes * 32 + 60) : 0;
  const tapeX = beltX + beltW - tapeW;

  // Vertical stripe pips inside tape band — real gi-belt stripes are vertical
  // tape strips wrapped around the belt end (perpendicular to belt length).
  // Tall + narrow.
  const stripePipW = 14;
  const stripeGap = 18;
  const stripesTotalW = maxStripes > 0 ? maxStripes * stripePipW + (maxStripes - 1) * stripeGap : 0;
  const tapeContentStartX = maxStripes > 0 ? tapeX + (tapeW - stripesTotalW) / 2 : 0;
  const pipY = beltY + 10;
  const pipH = beltH - 20;
  const stripePips = maxStripes > 0 ? Array.from({ length: maxStripes }).map((_, i) => {
    const filled = i < stripeCount;
    const x = tapeContentStartX + i * (stripePipW + stripeGap);
    return filled
      ? `<rect x="${x}" y="${pipY}" width="${stripePipW}" height="${pipH}" rx="1" fill="${tapeColor}" />`
      : `<rect x="${x}" y="${pipY}" width="${stripePipW}" height="${pipH}" rx="1" fill="none" stroke="${tapeColor}" stroke-width="2" stroke-dasharray="6 4" opacity="0.55" />`;
  }).join('') : '';

  // Header copy
  const tenureLine = tenureFromPrev ? `${xml(tenureFromPrev)} at the previous rank` : '';
  const promoDate = formatLongDate(promotion.promotedAt);
  const school = promotion.school ? xml(promotion.school) : '';
  const by = promotion.by ? `Promoted by ${xml(promotion.by)}` : '';

  // Layout positions adapt per aspect ratio
  const headerY = ratio === 'story' ? 280 : 130;
  const eyebrowY = headerY;
  const titleY = eyebrowY + 90;
  const rankY = titleY + 110;
  const detailsY = beltY + beltH + 90;

  const wordmarkY = H - (ratio === 'story' ? 280 : 180);
  const taglineY = wordmarkY + 60;
  const urlY = taglineY + 50;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
    <defs>
      <linearGradient id="bg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stop-color="#0a0a0a" />
        <stop offset="100%" stop-color="#000000" />
      </linearGradient>
      <radialGradient id="accentGlow" cx="50%" cy="${beltY / H * 100 + 5}%" r="40%">
        <stop offset="0%" stop-color="${accent}" stop-opacity="0.25" />
        <stop offset="100%" stop-color="${accent}" stop-opacity="0" />
      </radialGradient>
      <pattern id="weave" x="0" y="0" width="3" height="3" patternUnits="userSpaceOnUse">
        <rect width="2" height="3" fill="rgba(0,0,0,0.25)" />
      </pattern>
    </defs>

    <!-- Background -->
    <rect width="${W}" height="${H}" fill="url(#bg)" />
    <rect width="${W}" height="${H}" fill="url(#accentGlow)" />

    <!-- Top eyebrow -->
    <text x="${W / 2}" y="${eyebrowY}" font-family="ui-monospace, SFMono-Regular, Menlo, monospace" font-size="22" font-weight="700" letter-spacing="8" fill="${accent}" text-anchor="middle">${mode === 'current' ? `${xml((art?.shortName || '').toUpperCase())} · STILL TRAINING` : `PROMOTION · ${xml((art?.shortName || '').toUpperCase())}`}</text>

    <!-- Headline — different for promotion event vs current-state share.
         User-customizable via the share modal; falls back to mode-default. -->
    <text x="${W / 2}" y="${titleY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="${ratio === 'story' ? 130 : 110}" font-weight="900" letter-spacing="-1" fill="#ffffff" text-anchor="middle">${xml((headline || (mode === 'current' ? 'STANDING AT' : 'TEMPERED TO')).toUpperCase())}</text>

    <!-- Rank name -->
    <text x="${W / 2}" y="${rankY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="${ratio === 'story' ? 150 : 130}" font-weight="900" letter-spacing="-2" fill="${accent}" text-anchor="middle">${xml((rank?.name || '').toUpperCase())}</text>

    <!-- Belt strip with art-accent glow -->
    <g filter="drop-shadow(0 8px 24px rgba(0,0,0,0.6))">
      <rect x="${beltX}" y="${beltY}" width="${beltW}" height="${beltH}" rx="6" fill="${beltColor}" />
      <rect x="${beltX}" y="${beltY}" width="${beltW}" height="${beltH}" rx="6" fill="url(#weave)" opacity="0.18" />
      ${maxStripes > 0 ? `
        <rect x="${tapeX}" y="${beltY}" width="${tapeW}" height="${beltH}" rx="0" fill="${darkBelt ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)'}" />
        <line x1="${tapeX}" y1="${beltY}" x2="${tapeX}" y2="${beltY + beltH}" stroke="${tapeColor}" stroke-opacity="0.4" stroke-width="3" />
        ${stripePips}
      ` : ''}
      <rect x="${beltX}" y="${beltY}" width="${beltW}" height="${beltH}" rx="6" fill="none" stroke="${accent}" stroke-width="3" opacity="0.7" />
    </g>

    <!-- Details under belt -->
    ${athleteName ? `<text x="${W / 2}" y="${detailsY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="56" font-weight="700" fill="#ffffff" text-anchor="middle">${xml(athleteName)}</text>` : ''}
    ${tenureLine ? `<text x="${W / 2}" y="${detailsY + (athleteName ? 60 : 0)}" font-family="ui-sans-serif, system-ui" font-size="34" fill="#a1a1aa" text-anchor="middle">${tenureLine}</text>` : ''}
    ${promoDate ? `<text x="${W / 2}" y="${detailsY + (athleteName ? 110 : 50)}" font-family="ui-sans-serif, system-ui" font-size="32" fill="#d4d4d8" text-anchor="middle">${xml(promoDate)}</text>` : ''}
    ${school ? `<text x="${W / 2}" y="${detailsY + (athleteName ? 160 : 100)}" font-family="ui-sans-serif, system-ui" font-size="32" fill="#d4d4d8" text-anchor="middle">${school}</text>` : ''}
    ${by ? `<text x="${W / 2}" y="${detailsY + (athleteName ? 210 : 150)}" font-family="ui-sans-serif, system-ui" font-size="28" fill="#a1a1aa" text-anchor="middle" font-style="italic">${by}</text>` : ''}

    <!-- Bottom: wordmark + tagline + URL. Premium tier removes tagline + URL,
         wordmark stays as a brand-integrity floor (white-label would be separate).
         QR is non-premium only — premium users want a clean card. -->
    <text x="${W / 2}" y="${wordmarkY}" font-family="Impact, 'Anton', 'Bebas Neue', system-ui, sans-serif" font-size="${premium ? 48 : 68}" font-weight="900" letter-spacing="${premium ? 4 : 6}" fill="${premium ? '#52525b' : '#ffffff'}" text-anchor="middle">ENFERNO</text>
    ${premium ? '' : `<text x="${W / 2}" y="${taglineY}" font-family="ui-sans-serif, system-ui" font-size="26" fill="${accent}" text-anchor="middle" font-style="italic">${xml(tagline || 'Bring the heat.')}</text>`}
    ${premium ? '' : `<text x="${W / 2}" y="${urlY}" font-family="ui-monospace, monospace" font-size="22" fill="#71717a" text-anchor="middle" letter-spacing="3">enferno.app</text>`}
    ${(!premium && qrDataUrl) ? `<image href="${qrDataUrl}" x="${W - 200}" y="${H - 200}" width="140" height="140" opacity="0.85" />` : ''}
  </svg>`;
};

// Convert SVG string → PNG blob via offscreen canvas.
const svgToPngBlob = async (svgStr, width, height) => {
  const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    return await new Promise((res) => canvas.toBlob(res, 'image/png', 0.95));
  } finally {
    URL.revokeObjectURL(url);
  }
};

const HEADLINE_MAX = 22;
const HEADLINE_DEFAULTS = {
  current: 'STANDING AT',
  promotion: 'TEMPERED TO',
};
const HEADLINE_CHIPS = {
  current: ['STANDING AT', 'WEARING THE', 'STILL EARNING', 'IN THE'],
  promotion: ['TEMPERED TO', 'EARNED MY', 'EVERY REP TO', 'PROMOTED TO'],
};

const PromotionShareCard = ({ art, promotion, previousPromotion, athleteName, mode = 'promotion', premium = false, onClose }) => {
  const [ratio, setRatio] = useState('square'); // 'square' | 'story'
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(''); // transient confirmation message
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [allowAmplify, setAllowAmplify] = useState(false);
  const [headline, setHeadline] = useState(HEADLINE_DEFAULTS[mode] || HEADLINE_DEFAULTS.promotion);
  const previewRef = useRef(null);
  const { profile, addAmplificationConsent } = useProfile();

  const rank = art?.ranks?.find((r) => r.id === promotion.rankId);
  const tenureFromPrev = previousPromotion
    ? formatTenure(previousPromotion.promotedAt, new Date(promotion.promotedAt))
    : null;

  // Deterministic card id so a user re-sharing the same promotion doesn't
  // double-record consent. {art}-{rank}-{stripes}-{promotedAt}-{mode}.
  const cardId = useMemo(
    () => `promo-${art?.id || 'unknown'}-${promotion.rankId || 'unknown'}-${promotion.stripes || 0}-${promotion.promotedAt || 'undated'}-${mode}`,
    [art, promotion, mode]
  );

  // Capture consent on any share action if the box is checked.
  const captureConsent = () => {
    if (allowAmplify) addAmplificationConsent({ cardId, type: mode === 'current' ? 'rank-current' : 'promotion' });
  };

  // Stable tagline per share-card session (regenerating per modal-open keeps
  // copy fresh across multiple shares but doesn't thrash on aspect-ratio toggle).
  const tagline = useMemo(() => taglineFor(mode === 'current' ? 'current' : 'promotion'), [mode]);

  // Generate QR on mount. Branded with accent-tinted dark+light for contrast on
  // the dark card background. Non-premium only — premium cards stay clean.
  useEffect(() => {
    if (premium) return;
    let cancelled = false;
    QRCode.toDataURL('https://enferno.app/welcome/promotion', {
      errorCorrectionLevel: 'M',
      margin: 1,
      color: { dark: '#ffffff', light: '#00000000' },
      width: 280,
    }).then((url) => { if (!cancelled) setQrDataUrl(url); }).catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [premium]);

  const svgStr = useMemo(
    () => renderCardSVG({ ratio, art, rank, promotion, tenureFromPrev, athleteName, mode, premium, tagline, qrDataUrl, headline }),
    [ratio, art, rank, promotion, tenureFromPrev, athleteName, mode, premium, tagline, qrDataUrl, headline]
  );
  const width = 1080;
  const height = ratio === 'story' ? 1920 : 1080;

  // Generate PNG blob lazily (only when user acts)
  const getBlob = async () => svgToPngBlob(svgStr, width, height);

  const filename = `enferno-${(rank?.name || 'promotion').toLowerCase().replace(/\s+/g, '-')}-${promotion.promotedAt || 'card'}-${ratio}.png`;

  const flash = (msg) => {
    setStatus(msg);
    window.setTimeout(() => setStatus(''), 2500);
  };

  const handleShare = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      if (!blob) throw new Error('Render failed');
      const file = new File([blob], filename, { type: 'image/png' });
      // Web Share API: covers iOS/Android share sheet → IG, FB, X, Threads,
      // Messenger, iMessage, AirDrop, Discord, etc. — one button, every platform.
      const rankName = rank?.name || (mode === 'current' ? 'my rank' : 'a new rank');
      const shortName = art?.shortName || '';
      // Build a sentence-case version of whatever headline the user typed,
      // so share-sheet copy reads as prose instead of SCREAMING ALL CAPS.
      const headlineSentence = (headline || HEADLINE_DEFAULTS[mode] || '')
        .toLowerCase()
        .replace(/^./, (c) => c.toUpperCase());
      const shareTitle = mode === 'current'
        ? `${headlineSentence} ${rankName} — ${shortName}`
        : `${headlineSentence} ${rankName}`;
      const shareText = mode === 'current'
        ? `${headlineSentence} ${rankName} in ${shortName}. Still on the mats. Bring the heat. enferno.app`
        : `${headlineSentence} ${rankName} — ${shortName}. Bring the heat. enferno.app`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: shareTitle,
          text: shareText,
        });
        captureConsent();
        flash('Shared');
      } else {
        // Fallback: download. Native Share unsupported (desktop Chrome/Firefox).
        triggerDownload(blob);
        captureConsent();
        flash('Saved — share sheet not supported on this device');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') flash('Share cancelled or failed');
    } finally {
      setBusy(false);
    }
  };

  const triggerDownload = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      if (blob) {
        triggerDownload(blob);
        captureConsent();
        flash('Downloaded');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      // Clipboard API for images is supported in modern Chrome / Safari / Edge.
      // For browsers that lack it, we offer Download as the next-best fallback.
      if (blob && navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        captureConsent();
        flash('Copied to clipboard');
      } else {
        if (blob) triggerDownload(blob);
        captureConsent();
        flash('Clipboard unsupported — downloaded instead');
      }
    } catch {
      flash('Copy failed');
    } finally {
      setBusy(false);
    }
  };

  // Render the SVG inline in the preview via data URL (lighter than blob churn)
  const previewSrc = useMemo(() => {
    return `data:image/svg+xml;utf8,${encodeURIComponent(svgStr)}`;
  }, [svgStr]);

  // Tab/escape close handled by parent overlay click; ESC for keyboard
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const supportsWebShare = typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [new File([new Blob()], 'test.png', { type: 'image/png' })] });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/85" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-enf-surface-deep border border-enf-surface-border rounded-xl p-6" data-testid="promotion-share-card-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-enf-accent-fg font-semibold mb-1 flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> {mode === 'current' ? 'Share your rank' : 'Share this promotion'}
            </div>
            <div className="font-display text-3xl tracking-wide leading-none">{mode === 'current' ? 'Wear it proudly.' : 'Mark the moment, publicly.'}</div>
          </div>
          <button onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary" aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {/* Headline editor — user-customizable, capped at HEADLINE_MAX chars.
            Empty falls back to the mode default. Auto-uppercases so what they
            see in the input matches what lands on the card. */}
        <div className="mb-4" data-testid="headline-editor">
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="card-headline" className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold">Your headline</label>
            <span className={`text-[10px] font-mono ${headline.length >= HEADLINE_MAX ? 'text-enf-role-warning' : 'text-enf-text-muted'}`} data-testid="headline-char-count">
              {headline.length} / {HEADLINE_MAX}
            </span>
          </div>
          <input
            id="card-headline"
            type="text"
            value={headline}
            maxLength={HEADLINE_MAX}
            onChange={(e) => setHeadline(e.target.value.toUpperCase())}
            placeholder={HEADLINE_DEFAULTS[mode]}
            data-testid="headline-input"
            className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-enf-text-primary placeholder:text-enf-text-muted focus:outline-none focus:border-enf-accent-fg font-display tracking-wide text-lg"
          />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(HEADLINE_CHIPS[mode] || []).map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => setHeadline(chip)}
                data-testid={`headline-chip-${chip.replace(/\s+/g, '-').toLowerCase()}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wider border transition-colors ${
                  headline === chip
                    ? 'bg-enf-accent border-enf-accent-fg text-enf-accent-contrast'
                    : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary'
                }`}
              >
                {chip}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setHeadline(HEADLINE_DEFAULTS[mode] || '')}
              className="px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wider border border-enf-surface-border bg-transparent text-enf-text-muted hover:text-enf-text-primary transition-colors"
              data-testid="headline-reset"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Aspect ratio toggle */}
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mr-2">Format</span>
          <button
            onClick={() => setRatio('square')}
            data-testid="share-ratio-square"
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${ratio === 'square' ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-mid text-enf-text-secondary hover:bg-enf-surface-elevated'}`}
          >
            <ImageIcon className="w-3.5 h-3.5 inline mr-1.5" /> Square 1:1
            <span className="text-[10px] text-enf-text-primary/60 ml-1.5">IG Feed · FB · X</span>
          </button>
          <button
            onClick={() => setRatio('story')}
            data-testid="share-ratio-story"
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${ratio === 'story' ? 'bg-enf-accent text-enf-text-primary' : 'bg-enf-surface-mid text-enf-text-secondary hover:bg-enf-surface-elevated'}`}
          >
            <Smartphone className="w-3.5 h-3.5 inline mr-1.5" /> Story 9:16
            <span className="text-[10px] text-enf-text-primary/60 ml-1.5">IG Story · TikTok</span>
          </button>
        </div>

        {/* Preview */}
        <div className="mb-5 bg-enf-surface-mid rounded-lg overflow-hidden border border-enf-surface-border flex items-center justify-center" style={{ maxHeight: '60vh' }}>
          <img
            ref={previewRef}
            src={previewSrc}
            alt={`${rank?.name || ''} promotion share card preview`}
            data-testid="share-card-preview"
            className={ratio === 'story' ? 'max-h-[60vh] w-auto' : 'w-full max-h-[60vh] object-contain'}
          />
        </div>

        {/* Status flash */}
        {status && (
          <div className="mb-3 px-3 py-2 bg-enf-accent/10 border border-enf-accent-fg/30 text-enf-accent-fg text-sm rounded-md flex items-center gap-2" data-testid="share-status">
            <Check className="w-4 h-4" /> {status}
          </div>
        )}

        {/* Amplification consent — Recognition Layer 1. Per-card opt-in,
            default OFF. Per brand axiom (user defines what counts), we never
            assume amplification is wanted. */}
        <label className="mb-3 p-3 bg-enf-surface-deep border border-enf-surface-border rounded-md flex items-start gap-3 cursor-pointer hover:border-enf-surface-border transition-colors">
          <input
            type="checkbox"
            checked={allowAmplify}
            onChange={(e) => setAllowAmplify(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-enf-accent flex-shrink-0"
            data-testid="promotion-amplify-consent"
          />
          <span className="text-xs text-enf-text-secondary leading-relaxed">
            <span className="inline-flex items-center gap-1.5 text-enf-role-info font-semibold uppercase tracking-widest mr-1">
              <Megaphone className="w-3 h-3" /> Optional
            </span>
            Allow <span className="text-enf-text-primary font-semibold">@EnfernoApp</span> to amplify this card on our official channels.
            {!profile.socialHandle && (
              <span className="block mt-1 text-[11px] text-enf-text-muted italic">Set a @handle in your profile to be recognizable.</span>
            )}
          </span>
        </label>

        {/* Actions — all surfaces in one row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            onClick={handleShare}
            disabled={busy}
            data-testid="share-native-button"
            className="py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <Share2 className="w-4 h-4" /> {supportsWebShare ? 'Share…' : 'Share / Save'}
          </button>
          <button
            onClick={handleDownload}
            disabled={busy}
            data-testid="share-download-button"
            className="py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Download PNG
          </button>
          <button
            onClick={handleCopy}
            disabled={busy}
            data-testid="share-copy-button"
            className="py-3 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            <Copy className="w-4 h-4" /> Copy image
          </button>
        </div>

        <div className="mt-4 text-[11px] text-enf-text-muted leading-relaxed">
          Mobile: <strong className="text-enf-text-secondary">Share…</strong> opens the system sheet — Instagram (Feed + Story), Facebook, X, Threads, Discord, Messenger, iMessage, AirDrop, all in one tap.
          Desktop: native share isn't supported in most browsers, so use <strong className="text-enf-text-secondary">Download</strong> or <strong className="text-enf-text-secondary">Copy image</strong> and paste straight in.
        </div>
      </div>
    </div>
  );
};

export default PromotionShareCard;
